import type { LangType, TranslationFunction } from "@/types/i18n";
import en from "./en.ts";
import ja from "./ja.ts";
import { logger } from "@/utils/logger.ts";
export const defaultLang = "ja";

export const ui = {
  en,
  ja,
} as const;

export const Langs = Object.keys(ui) as (keyof typeof ui)[];

/**
 * Retrieves the language from the given URL.
 *
 * @param url - The URL object representing the URL to extract the language from.
 * @returns The language code extracted from the URL, or the default language if not found.
 */
export const getLanguageFromUrl = (url: URL): LangType => {
  const [, lang] = url.pathname.split("/");
  if (lang in ui) {
    return lang as LangType;
  }

  return defaultLang;
};

/**
 * Returns a translations object and a function to localize URLs based on the current language.
 *
 * @param lang - The current language. Defaults to the default language.
 * @returns An object containing the translations and the localizeUrl function.
 */
export function useTranslations(lang: LangType = defaultLang) {
  if (!(lang in ui)) {
    logger.warn(
      `Language "${lang}" not found. Falling back to the default language.`,
    );
  }
  /**
   * Translates the given key to the corresponding translation in the current language.
   * If the translation is not available in the current language, it falls back to the default language.
   *
   * @param key - The key to be translated.
   */
  const t: TranslationFunction = (key) => {
    if (!key) {
      logger.warn("Translation key is undefined.");
      return "";
    }

    if (ui[lang] && key in ui[lang]) {
      const translation = ui[lang][key as keyof (typeof ui)[LangType]];
      if (translation) {
        return translation;
      }
    }
    logger.warn(
      `Translation for key "${key}" not found in language "${lang}". Falling back to the default language.`,
    );
    if (ui[defaultLang] && key in ui[defaultLang]) {
      const translation = ui[defaultLang][key as keyof (typeof ui)[LangType]];
      if (translation) {
        return translation;
      }
    }
    logger.warn(
      `Translation for key "${key}" not found in the default language.`,
    );

    return "";
  };

  /**
   * Localizes the given URL by appending the language code.
   *
   * @param url - The URL to be localized.
   * @returns The localized URL.
   */
  const localizeUrl = (url: string) => (lang === "ja" ? url : `/${lang}${url}`);

  return { t, localizeUrl };
}
